/**
 * 0287-G — Banner in-chat (§0.3): aviso cuando existen datos sugeridos por IA listos para revisar en el perfil 360.
 */
import React, { useMemo, useState, useEffect } from "react";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from "@material-ui/icons/Close";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";

const ORANGE_BORDER = "rgba(255, 152, 0, 0.58)";
const ORANGE_BG = "rgba(255, 152, 0, 0.13)";
const BLUE_LINK = "#0056b3";

function parseJsonObject(raw) {
  if (!raw) return {};
  if (typeof raw === "object" && raw !== null && !Array.isArray(raw)) return raw;
  if (typeof raw === "string") {
    try {
      const o = JSON.parse(raw);
      return o && typeof o === "object" && !Array.isArray(o) ? o : {};
    } catch (_) {
      return {};
    }
  }
  return {};
}

function storageDismissKey(contactId, ai) {
  if (!contactId) return "";
  const keysSig = ai && typeof ai === "object" ? Object.keys(ai).sort().join("|").slice(0, 260) : "";
  return `0287_ia_brief:${contactId}:${keysSig}`;
}

function hasMeaningfulAiHints(aiExtractedData) {
  const ai = aiExtractedData;
  if (!ai || typeof ai !== "object") return false;
  return Object.keys(ai).some((k) => {
    const v = ai[k];
    if (v == null || v === "") return false;
    if (typeof v === "object" && v !== null && !Array.isArray(v)) {
      if (typeof v.confidence === "number" && Number.isFinite(v.confidence)) {
        if (v.confidence >= 0.5) return true;
      }
      if (typeof v.value === "string" && v.value.trim().length >= 2) return true;
      return false;
    }
    if (typeof v === "string" && v.trim().length >= 2) return true;
    if (typeof v === "number" && Number.isFinite(v)) return true;
    if (typeof v === "boolean") return true;
    return false;
  });
}

/** Evita repetir tras edición manual reciente desde perfil 360 (lastManualProfileEditAt en profileData). */
function recentlyEditedManual(profileData) {
  if (!profileData || typeof profileData !== "object") return false;
  const raw = profileData.lastManualProfileEditAt;
  if (raw == null || raw === "") return false;
  const t = new Date(raw).getTime();
  if (Number.isNaN(t)) return false;
  return Date.now() - t < 90 * 1000;
}

export default function IaChatBriefingBanner({ contact, ticket, onOpenProfile360 }) {
  const [dismissKey, setDismissKey] = useState("");

  const visible = useMemo(() => {
    const ai = parseJsonObject(contact?.aiExtractedData);
    const pd = parseJsonObject(contact?.profileData);
    if (!ticket || ticket.isGroup) return false;
    if (!contact || !contact.id) return false;
    if (!hasMeaningfulAiHints(ai)) return false;
    if (recentlyEditedManual(pd)) return false;
    return true;
  }, [ticket, contact?.id, contact?.aiExtractedData, contact?.profileData]);

  useEffect(() => {
    const ai = parseJsonObject(contact?.aiExtractedData);
    const k = visible && contact?.id ? storageDismissKey(contact.id, ai) : "";
    setDismissKey(k);
  }, [visible, contact?.id, contact?.aiExtractedData]);

  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!visible || typeof window === "undefined" || !dismissKey) {
      setDismissed(false);
      return;
    }
    try {
      setDismissed(sessionStorage.getItem(dismissKey) === "1");
    } catch (_) {
      setDismissed(false);
    }
  }, [visible, dismissKey]);

  if (!visible || dismissed) return null;

  const handleDismiss = () => {
    try {
      if (dismissKey) sessionStorage.setItem(dismissKey, "1");
    } catch (_) {
      /* noop */
    }
    setDismissed(true);
  };

  const openSheet = typeof onOpenProfile360 === "function" ? () => onOpenProfile360() : undefined;

  return (
    <Paper
      role="status"
      aria-live="polite"
      elevation={0}
      style={{
        margin: "8px 12px",
        padding: "10px 38px 10px 14px",
        borderRadius: 10,
        position: "relative",
        border: `1px solid ${ORANGE_BORDER}`,
        backgroundColor: ORANGE_BG
      }}
    >
      <IconButton
        size="small"
        aria-label="Cerrar aviso IA"
        onClick={handleDismiss}
        style={{ position: "absolute", top: 4, right: 4, padding: 4 }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
        <InfoOutlinedIcon style={{ color: "#E65100", marginTop: 2 }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" style={{ fontWeight: 700, color: "#E65100" }}>
            Preferencias detectadas por la IA
          </Typography>
          <Typography variant="body2" style={{ marginTop: 4, color: "#424242", lineHeight: 1.45 }}>
            Hay datos sugeridos en la ficha de este cliente. Revísalos y confírmalos cuando quieras.
          </Typography>
          {typeof openSheet === "function" ? (
            <Button size="small" color="primary" style={{ marginTop: 10, fontWeight: 600, color: BLUE_LINK }} onClick={openSheet}>
              Datos del contacto
            </Button>
          ) : null}
        </div>
      </div>
    </Paper>
  );
}
